/**
 * Suppliers List Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useSuppliers hook.
 */

import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Loader2, Plus, ChevronDown, Truck, UserPlus, CheckCircle2 } from 'lucide-react';
import { SectionTourTrigger } from '../components/tour/SectionTourTrigger';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { suppliersAPI, coreAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useRegisterPageRefresh } from '../contexts/PageRefreshContext';
import {
  useSuppliers,
  SupplierMap,
  SupplierGrid,
  SearchFilterBar,
} from './suppliers-list';

interface LogisticsFormData {
  company_name: string;
  address: string;
  postcode: string;
  phone: string;
  email: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
}

const INITIAL_LOGISTICS_FORM: LogisticsFormData = {
  company_name: '',
  address: '',
  postcode: '',
  phone: '',
  email: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_email: '',
  contact_phone: '',
};

export default function Suppliers() {
  const {
    navigate,
    loading,
    error,
    suppliers,
    filteredSuppliers,
    mapLocations,
    currentTab,
    setCurrentTab,
    setSelectedGroupName,
    showFilters,
    setShowFilters,
    filters,
    setFilters,
    riskCounts,
    questionnaireCounts,
    siteOptions,
    commodityGroupOptions,
    commodityTypeOptions,
    supplierCodeOptions,
    searchRef,
    searchQuery,
    handleSearch,
    handleSearchKeyDown,
    handleClearSearch,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    paginatedSuppliers,
    fetchData,
  } = useSuppliers();
  useRegisterPageRefresh(fetchData);
  const { user } = useAuth();
  const { isFeatureEnabled, isMenuItemVisible } = useMenuSettings();
  // Logistics company option is only relevant when Call Off is active. The flag is now
  // driven by per-supplier SupplierCustomerRelationship.call_off_enabled.
  const showLogisticsOption = isMenuItemVisible('callOff') && isFeatureEnabled('switch_call_off_tab');

  // Logistics modal state
  const [showLogisticsModal, setShowLogisticsModal] = useState(false);
  const [logisticsForm, setLogisticsForm] = useState<LogisticsFormData>(INITIAL_LOGISTICS_FORM);
  const [logisticsSaving, setLogisticsSaving] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [addSupplierForm, setAddSupplierForm] = useState({
    company_name: '', supplier_code: '', registration_number: '',
    primary_email: '', primary_phone: '', website: '',
    address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '', country: '',
    payment_terms: '', currency: 'GBP', annual_revenue: '', employee_count: '', year_established: '',
    description: '',
  });
  const [addSupplierSaving, setAddSupplierSaving] = useState(false);

  const handleLogisticsSubmit = async () => {
    if (!logisticsForm.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (logisticsForm.contact_email.trim() && (!logisticsForm.contact_first_name.trim() || !logisticsForm.contact_last_name.trim())) {
      toast.error('Contact first and last name are required when providing an email');
      return;
    }

    setLogisticsSaving(true);
    try {
      const { default: api } = await import('../lib/api/client');
      await api.post('/call-off/logistics/links/add-company/', {
        company_name: logisticsForm.company_name.trim(),
        address: logisticsForm.address.trim(),
        postcode: logisticsForm.postcode.trim(),
        phone: logisticsForm.phone.trim(),
        email: logisticsForm.email.trim(),
        contact_first_name: logisticsForm.contact_first_name.trim(),
        contact_last_name: logisticsForm.contact_last_name.trim(),
        contact_email: logisticsForm.contact_email.trim(),
        contact_phone: logisticsForm.contact_phone.trim(),
      });
      toast.success('Logistics company added successfully');
      setShowLogisticsModal(false);
      setLogisticsForm(INITIAL_LOGISTICS_FORM);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add logistics company';
      toast.error(message);
    } finally {
      setLogisticsSaving(false);
    }
  };

  const handleAddSupplierSubmit = async () => {
    if (!addSupplierForm.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setAddSupplierSaving(true);
    try {
      const profile = user?.profile_id ? await coreAPI.getMyProfile() : null;
      const customerId = profile?.data?.company || user?.company;
      const f = addSupplierForm;
      await suppliersAPI.createFromWizard({
        company_name: f.company_name.trim(),
        company_address: [f.address_line_1, f.city, f.postal_code, f.country].filter(Boolean).join(', '),
        currency: f.currency,
        description: f.description.trim(),
        customer_id: customerId || 0,
      });
      // Update company with extra fields after creation
      // (supplier_code, registration_number, etc. are set via the supplier detail settings)
      toast.success(`Supplier "${addSupplierForm.company_name}" added successfully`);
      setShowAddSupplierModal(false);
      setAddSupplierForm({
        company_name: '', supplier_code: '', registration_number: '',
        primary_email: '', primary_phone: '', website: '',
        address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '', country: '',
        payment_terms: '', currency: 'GBP', annual_revenue: '', employee_count: '', year_established: '',
        description: '',
      });
      fetchData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add supplier';
      toast.error(message);
    } finally {
      setAddSupplierSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading suppliers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
   <div className="space-y-6">
        {/* Header with Add Dropdown */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Suppliers</h1>
            <p className="text-muted-foreground">
              Approved supplier directory
            </p>
          </div>
          <div className="flex items-center gap-3">
            <SectionTourTrigger
              pageKey="suppliers"
              sectionKey="list"
              variant="link"
              label="Tour this page"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-tour="add-supplier-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/onboarding')}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Onboard New Supplier
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowAddSupplierModal(true)}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Add Approved Supplier
                </DropdownMenuItem>
                {showLogisticsOption && (
                  <DropdownMenuItem onClick={() => setShowLogisticsModal(true)}>
                    <Truck className="h-4 w-4 mr-2" />
                    Add Logistics Company
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Content */}
        <Card className="p-0">
          <CardContent className="p-6">
            <div className="space-y-6">
              <SupplierMap
                locations={mapLocations}
                onViewDetails={(id) => navigate(`/suppliers/${id}`)}
              />
              <SupplierGrid
                suppliers={suppliers}
                filteredSuppliers={filteredSuppliers}
                paginatedSuppliers={paginatedSuppliers}
                currentTab={currentTab}
                onTabChange={(tab) => { setCurrentTab(tab); if (tab === 'groups') setSelectedGroupName(null); }}
                onNavigate={navigate}
                onSelectGroup={(groupName) => {
                  // CR-4578: When user clicks a Supplier Group card, switch to sites tab
                  // filtered by that group
                  setSelectedGroupName(groupName);
                  setCurrentTab('sites');
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                searchFilterBar={
                  <SearchFilterBar
                    searchRef={searchRef}
                    showFilters={showFilters}
                    filters={filters}
                    riskCounts={riskCounts}
                    questionnaireCounts={questionnaireCounts}
                    siteOptions={siteOptions}
                    commodityGroupOptions={commodityGroupOptions}
                    commodityTypeOptions={commodityTypeOptions}
                    supplierCodeOptions={supplierCodeOptions}
                    searchQuery={searchQuery}
                    onSearch={handleSearch}
                    onSearchKeyDown={handleSearchKeyDown}
                    onClearSearch={handleClearSearch}
                    onToggleFilters={() => setShowFilters(!showFilters)}
                    onFiltersChange={setFilters}
                  />
                }
              />
            </div>
          </CardContent>
        </Card>
          
      {/* Add Approved Supplier Modal */}
      <Dialog open={showAddSupplierModal} onOpenChange={setShowAddSupplierModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Approved Supplier</DialogTitle>
            <DialogDescription>Add a new supplier with company details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2">
            {/* Supplier Identification */}
            <div>
              <h4 className="text-sm font-semibold mb-3">Supplier Identification</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Company Name *</Label>
                  <Input value={addSupplierForm.company_name} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, company_name: e.target.value })} placeholder="e.g. Acme Ltd" />
                </div>
                <div className="space-y-1.5">
                  <Label>Supplier Code</Label>
                  <Input value={addSupplierForm.supplier_code} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, supplier_code: e.target.value })} placeholder="e.g. SUP-001" />
                </div>
                <div className="space-y-1.5">
                  <Label>Company Number</Label>
                  <Input value={addSupplierForm.registration_number} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, registration_number: e.target.value })} placeholder="e.g. 12345678" />
                </div>
              </div>
            </div>
            {/* Payment & Financial */}
            <div>
              <h4 className="text-sm font-semibold mb-3">Payment & Financial</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Payment Terms</Label>
                  <Input value={addSupplierForm.payment_terms} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, payment_terms: e.target.value })} placeholder="e.g. Net 30" />
                </div>
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <select value={addSupplierForm.currency} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, currency: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm bg-background">
                    {['GBP', 'USD', 'EUR', 'PLN', 'CAD', 'AUD', 'CHF', 'SEK', 'NOK', 'DKK', 'CZK', 'JPY', 'CNY', 'INR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Annual Revenue</Label>
                  <Input value={addSupplierForm.annual_revenue} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, annual_revenue: e.target.value })} placeholder="e.g. 5000000" />
                </div>
              </div>
            </div>
            {/* Business Details */}
            <div>
              <h4 className="text-sm font-semibold mb-3">Business Details</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Employee Count</Label>
                  <Input type="number" value={addSupplierForm.employee_count} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, employee_count: e.target.value })} placeholder="e.g. 250" />
                </div>
                <div className="space-y-1.5">
                  <Label>Year Established</Label>
                  <Input type="number" value={addSupplierForm.year_established} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, year_established: e.target.value })} placeholder="e.g. 1990" />
                </div>
              </div>
            </div>
            {/* Contact Information */}
            <div>
              <h4 className="text-sm font-semibold mb-3">Contact Information</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input type="email" value={addSupplierForm.primary_email} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, primary_email: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Telephone</Label>
                  <Input value={addSupplierForm.primary_phone} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, primary_phone: e.target.value })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Website</Label>
                  <Input value={addSupplierForm.website} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, website: e.target.value })} placeholder="https://" />
                </div>
              </div>
            </div>
            {/* Address */}
            <div>
              <h4 className="text-sm font-semibold mb-3">Address</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Address Line 1</Label>
                  <Input value={addSupplierForm.address_line_1} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, address_line_1: e.target.value })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Address Line 2</Label>
                  <Input value={addSupplierForm.address_line_2} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, address_line_2: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>City</Label>
                  <Input value={addSupplierForm.city} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, city: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>State / County</Label>
                  <Input value={addSupplierForm.state} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, state: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Postal Code</Label>
                  <Input value={addSupplierForm.postal_code} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, postal_code: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Input value={addSupplierForm.country} onChange={(e) => setAddSupplierForm({ ...addSupplierForm, country: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowAddSupplierModal(false)} disabled={addSupplierSaving}>Cancel</Button>
            <Button onClick={handleAddSupplierSubmit} disabled={addSupplierSaving}>
              {addSupplierSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Adding...</> : 'Add Supplier'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Logistics Company Modal */}
      <Dialog open={showLogisticsModal} onOpenChange={setShowLogisticsModal}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Add Logistics Company
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="logistics-name">
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="logistics-name"
                value={logisticsForm.company_name}
                onChange={(e) => setLogisticsForm(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Enter logistics company name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logistics-address">Address</Label>
              <Input
                id="logistics-address"
                value={logisticsForm.address}
                onChange={(e) => setLogisticsForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Enter address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="logistics-postcode">Postcode</Label>
                <Input
                  id="logistics-postcode"
                  value={logisticsForm.postcode}
                  onChange={(e) => setLogisticsForm(prev => ({ ...prev, postcode: e.target.value }))}
                  placeholder="Postcode"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="logistics-phone">Phone</Label>
                <Input
                  id="logistics-phone"
                  value={logisticsForm.phone}
                  onChange={(e) => setLogisticsForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logistics-email">Email</Label>
              <Input
                id="logistics-email"
                type="email"
                value={logisticsForm.email}
                onChange={(e) => setLogisticsForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contact@logistics.com"
              />
            </div>

            {/* Contact User Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-3">Contact User (Login Account)</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="logistics-contact-first">First Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="logistics-contact-first"
                      value={logisticsForm.contact_first_name}
                      onChange={(e) => setLogisticsForm(prev => ({ ...prev, contact_first_name: e.target.value }))}
                      placeholder="First name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="logistics-contact-last">Last Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="logistics-contact-last"
                      value={logisticsForm.contact_last_name}
                      onChange={(e) => setLogisticsForm(prev => ({ ...prev, contact_last_name: e.target.value }))}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logistics-contact-email">Login Email <span className="text-red-500">*</span></Label>
                  <Input
                    id="logistics-contact-email"
                    type="email"
                    value={logisticsForm.contact_email}
                    onChange={(e) => setLogisticsForm(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="user@logistics.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logistics-contact-phone">Phone</Label>
                  <Input
                    id="logistics-contact-phone"
                    value={logisticsForm.contact_phone}
                    onChange={(e) => setLogisticsForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="Phone number"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  A login account will be created with default password: <strong>Welcome123!</strong>
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogisticsModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleLogisticsSubmit} disabled={logisticsSaving}>
              {logisticsSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Logistics Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
